import React, {
  useRef,
  Fragment,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Shape, Transformer, Circle, Group } from "react-konva";
import { TModel } from "./models";

interface Props {
  //The scale of the stage
  scale: number;
  model: TModel;
  baseUrl: string;
  points: number[][];
  updatePoint: (index: number, point: number[]) => void;
  updatePoints: (points: number[][]) => void;
  outOfBounds: (x: number, y: number) => boolean;
  redraw: () => void;
  draggable: boolean;
  position: { x: number; y: number };
}

const Pitch = forwardRef((props: Props, ref) => {
  const pitchGroupRef = useRef<any>();
  const transformerRef = useRef<any>();
  const [selected, setSelected] = useState(false);
  //The boundaries of the pitch model

  const points = props.points;
  //If the points are moved then we calculate the boundaries of the pitch model
  const { minX, minY, maxX, maxY } = useMemo(() => {
    return {
      minX: points.map((p) => p[0]).sort((a, b) => a - b)[0],
      minY: points.map((p) => p[1]).sort((a, b) => a - b)[0],
      maxX: points.map((p) => p[0]).sort((a, b) => b - a)[0],
      maxY: points.map((p) => p[1]).sort((a, b) => b - a)[0],
    };
  }, [points]);

  React.useEffect(() => {
    if (selected && transformerRef.current) {
      // we need to attach transformer manually
      transformerRef.current.setNode(pitchGroupRef.current);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [selected]);

  //Update the clientRect whenever the boundaries change.
  React.useEffect(() => {
    pitchGroupRef.current.getClientRect = () => {
      const ret = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
      return ret;
    };
  }, [maxX, minX, maxY, minY]);

  useImperativeHandle(ref, () => ({
    getAlert() {
      alert("getAlert from Child");
    },
    getTransformedPoints() {
      // const pos = pitchGroupRef.current.getPosition();
      const tr = pitchGroupRef.current.getAbsoluteTransform().copy();
      return props.points.map((p) => {
        const trp = tr.point({ x: p[0], y: p[1] });
        return [trp.x, trp.y];
      });
    },
  }));

  const onDragMoveCircle = (index: number) => (e: any) => {
    // const tr = pitchGroupRef.current.getAbsoluteTransform().copy();
    props.updatePoint(index, [e.target.getX(), e.target.getY()]);
  };

  const onDragEndGroup = (_event: any) => {
    _event.cancelBubble = true;
  };

  const onTransformEnd = () => {};

  // //This tells the parent what the updated points are after applying
  // //the transformations on it (rotation and scale).
  // const updateResults = (ps: Array<Array<number>>) => {
  //   const tr = pitchGroupRef.current.getAbsoluteTransform().copy();
  //   props.updatePoints(ps.map((p) => transformPoint(tr, p[0], p[1])));
  // };

  /* We draw a Konva Group that consists of a Shape that draws the shapes
   * and lines of the pitch model. And besides that the group contains the individual
   * circles that can be moved
   */
  return (
    <Fragment>
      <Group
        key="group"
        draggable={props.draggable}
        ref={pitchGroupRef}
        onClick={() => setSelected(!selected)}
        onDragEnd={onDragEndGroup}
      >
        <Shape
          sceneFunc={(context, shape) => {
            if (points) {
              for (let rect of props.model.shapes) {
                if (points.length >= rect[0] && points.length >= rect[1]) {
                  context.beginPath();
                  let from = points[rect[0]];
                  if (from) {
                    context.moveTo(+from[0], +from[1]);
                    for (let i = 0; i < rect.length; i++) {
                      let to = points[rect[i]];
                      if (to) context.lineTo(+to[0], +to[1]);
                    }
                    context.closePath();
                  }
                  // (!) Konva specific method, it is very important
                  context.fillStrokeShape(shape);
                }
              }
              for (let line of props.model.lines) {
                const from = points[line[0]];
                const to = points[line[1]];
                if (to && from) {
                  context.beginPath();
                  context.moveTo(from[0], from[1]);
                  context.lineTo(to[0], to[1]);
                  context.closePath();
                  // (!) Konva specific method, it is very important
                  context.fillStrokeShape(shape);
                }
              }
            }
          }}
          fill="rgba(0,160,106,0.1)"
          stroke="black"
          dashEnabled={true}
          strokeWidth={2}
          strokeScaleEnabled={false}
        />
        {points &&
          points.map((point, index) => {
            return (
              <Fragment key={index}>
                {/*The first circle is the outer part, the second is the small black dot */}
                <Circle
                  key={index}
                  radius={
                    5 /
                    (props.scale *
                      (pitchGroupRef.current
                        ? pitchGroupRef.current.scaleX()
                        : 1))
                  }
                  x={point[0]}
                  y={point[1]}
                  stroke="black"
                  fill={
                    index === 0 ? "rgba(255,0,0, 0.2)" : "rgba(255,255,0, 0.2)"
                  }
                  strokeScaleEnabled={false}
                  strokeWidth={1}
                  draggable
                  onDragMove={onDragMoveCircle(index)}
                />
                <Circle
                  key={"dot" + index}
                  radius={
                    1 /
                    (props.scale *
                      (pitchGroupRef.current
                        ? pitchGroupRef.current.scaleX()
                        : 1))
                  }
                  x={point[0]}
                  y={point[1]}
                  stroke="black"
                  fill="rgba(0,0,0, 0.5)"
                  strokeScaleEnabled={false}
                  strokeWidth={1}
                  draggable
                  onDragMove={onDragMoveCircle(index)}
                />
              </Fragment>
            );
          })}
      </Group>
      {selected && (
        <Transformer
          key="transformer"
          ref={transformerRef}
          enabledAnchors={[
            "top-left",
            "top-right",
            "bottom-left",
            "bottom-right",
            "middle-left",
            "middle-right",
            "top-center",
            "bottom-center",
          ]}
          onTransformEnd={onTransformEnd}
        />
      )}
    </Fragment>
  );
});

export default Pitch;
